import Image from "next/image";
import Typography from "@mui/material/Typography";

import { StrapiComponentType, DeepSkyType } from "@/src/type";
import { StringHelper, MoneyHelper } from "@/src/util";

import styles from "./Facts.module.scss";

interface FactsSplitPricingProps {
  propertyUnitDetails: StrapiComponentType.Section.PropertyUnitDetails;
  floorplan: DeepSkyType.AssetType.PropertyInventory.MarketingFloorPlan;
}

function FactsSplitPricing({ propertyUnitDetails, floorplan }: FactsSplitPricingProps) {
  const furnishedPrice = floorplan?.minFurnishedPrice ?? floorplan?.minPrice;

  function priceFormatter(currency?: string, price?: number) {
    if (!price || !currency) {
      return "";
    }
    const currencySymbol = MoneyHelper.getCurrencySymbol(currency);
    return `${currencySymbol}${StringHelper.addCommasToNumber(`${price}`)}`;
  }

  function priceIntervalFormatter(pricePeriod: string) {
    switch (pricePeriod) {
      case "P12M":
        return ` / ${propertyUnitDetails?.intervalYearsTitle}`;
      case "P1M":
      default:
        return ` / ${propertyUnitDetails?.intervalMonthsTitle}`;
    }
  }

  return (
    <div className={styles.splitPricingContainer}>
      {priceFormatter(floorplan.minPrice.currency, floorplan.minPrice.basePrice) && (
        <>
          <div className={styles.splitPricing}>
            <div className={styles.priceLabelContainer}>
              <Typography
                variant="body2"
                className={styles.pricingLabel}
              >
                {propertyUnitDetails?.unfurnishedStartingAtTitle}
              </Typography>
            </div>
            <div className={styles.priceContainer}>
              <Typography
                variant="body1"
                className={styles.price}
              >
                {priceFormatter(floorplan.minPrice.currency, floorplan.minPrice.basePrice)}
              </Typography>
              <Typography
                variant="body1"
                className={styles.priceInterval}
              >
                {priceIntervalFormatter(floorplan.minPrice.pricePeriod)}
              </Typography>
            </div>
          </div>
          {priceFormatter(furnishedPrice?.currency, furnishedPrice?.furnishedPrice) && <div className={styles.divider} />}
        </>
      )}
      {priceFormatter(furnishedPrice?.currency, furnishedPrice?.furnishedPrice) && (
        <div className={styles.splitPricing}>
          <div className={styles.priceLabelContainer}>
            <Image
              src="/image/icon_furnished.svg"
              alt="Beds Icon"
              width={32}
              height={24}
            />
            <Typography
              variant="body2"
              className={styles.pricingLabel}
            >
              {propertyUnitDetails?.furnishedStartingAtTitle}
            </Typography>
          </div>
          <div className={styles.priceContainer}>
            <Typography
              variant="body1"
              className={styles.price}
            >
              {priceFormatter(furnishedPrice?.currency, furnishedPrice?.furnishedPrice)}
            </Typography>
            <Typography
              variant="body1"
              className={styles.priceInterval}
            >
              {priceIntervalFormatter(furnishedPrice?.pricePeriod ?? "")}
            </Typography>
          </div>
        </div>
      )}
    </div>
  );
}

export default FactsSplitPricing;
